import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useToast } from "@chakra-ui/react";
import { getCommissionReportAPI } from "../../service/AuthService";

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const readValue = (row, key) => numberValue(row?.[key]);
const amountClassName = (value) => {
  const amount = numberValue(value);
  if (amount < 0) return "commission-amount-negative";
  if (amount > 0) return "commission-amount-positive";
  return "commission-amount-zero";
};
const AmountCell = ({ value }) => (
  <td className={amountClassName(value)}>{numberValue(value)}</td>
);
const canDrillInto = (row) =>
  String(row?.userType || "").toUpperCase() !== "CLIENT" &&
  row?.userId !== undefined &&
  row?.userId !== null;

const normalizeResponse = (response) => {
  return Array.isArray(response) ? response : [];
};

const getErrorMessage = (result) => {
  if (typeof result?.response === "string") return result.response;
  return result?.response?.message || result?.message || "Please try again.";
};

export default function CommissionUserwiseReport() {
  const [fromDate, setFromDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [rows, setRows] = useState([]);
  const [nameDrillPath, setNameDrillPath] = useState([]);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [childrenByParent, setChildrenByParent] = useState({});
  const [loadingUsers, setLoadingUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const getPayload = (parentUserId) => {
    const payload = {
      from: `${fromDate}T00:00:00`,
      to: `${toDate}T23:59:59`,
    };
    if (parentUserId !== undefined && parentUserId !== null) {
      payload.parentUserId = parentUserId;
    }
    return payload;
  };

  const requestRows = async (parentUserId) => {
    const result = await getCommissionReportAPI(getPayload(parentUserId));
    if (Number(result?.code) === 200 && Array.isArray(result?.response)) {
      return normalizeResponse(result.response);
    }

    toast({
      title: "Unable to load report",
      description: getErrorMessage(result),
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    return null;
  };

  const loadReport = async () => {
    const start = moment(fromDate, "YYYY-MM-DD", true);
    const end = moment(toDate, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
      toast({
        title: "Invalid date range",
        description: "The To date must be on or after the From date",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    const reportRows = await requestRows();
    if (reportRows !== null) {
      setRows(reportRows);
      setNameDrillPath([]);
      setExpandedUsers({});
      setChildrenByParent({});
    } else {
      setRows([]);
      setNameDrillPath([]);
      setExpandedUsers({});
      setChildrenByParent({});
    }
    setLoading(false);
  };

  useEffect(() => {
    loadReport();
    // Load once with the default seven-day range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleUser = async (row) => {
    if (!canDrillInto(row)) return;
    const userKey = String(row.userId);

    if (expandedUsers[userKey]) {
      setExpandedUsers((current) => ({ ...current, [userKey]: false }));
      return;
    }

    if (Object.prototype.hasOwnProperty.call(childrenByParent, userKey)) {
      setExpandedUsers((current) => ({ ...current, [userKey]: true }));
      return;
    }

    setLoadingUsers((current) => ({ ...current, [userKey]: true }));
    const childRows = await requestRows(row.userId);
    if (childRows !== null) {
      setChildrenByParent((current) => ({ ...current, [userKey]: childRows }));
      setExpandedUsers((current) => ({ ...current, [userKey]: true }));
    }
    setLoadingUsers((current) => ({ ...current, [userKey]: false }));
  };

  const openUserReport = async (row) => {
    if (!canDrillInto(row)) return;
    setLoading(true);
    const reportRows = await requestRows(row.userId);
    if (reportRows !== null) {
      setRows(reportRows);
      setNameDrillPath((currentPath) => [
        ...currentPath,
        { userId: row.userId, userName: row.userName || row.username || String(row.userId) },
      ]);
      setExpandedUsers({});
      setChildrenByParent({});
    }
    setLoading(false);
  };

  const goBackFromNameReport = async () => {
    const previousPath = nameDrillPath.slice(0, -1);
    const parentUserId = previousPath.length
      ? previousPath[previousPath.length - 1].userId
      : undefined;

    setLoading(true);
    const reportRows = await requestRows(parentUserId);
    if (reportRows !== null) {
      setRows(reportRows);
      setNameDrillPath(previousPath);
      setExpandedUsers({});
      setChildrenByParent({});
    }
    setLoading(false);
  };

  const totals = useMemo(
    () =>
      rows.reduce(
        (total, row) => ({
          marketBookmaker:
            total.marketBookmaker +
            readValue(row, "bookmaker"),
          marketFancy:
            total.marketFancy + readValue(row, "fancy"),
          denaBookmaker:
            total.denaBookmaker +
            readValue(row, "commDenaBookmaker"),
          denaFancy:
            total.denaFancy + readValue(row, "commDenaFancy"),
          denaTotal:
            total.denaTotal + readValue(row, "commDenaTotal"),
          lenaBookmaker:
            total.lenaBookmaker +
            readValue(row, "commLenaBookmaker"),
          lenaFancy:
            total.lenaFancy + readValue(row, "commLenaFancy"),
          lenaTotal:
            total.lenaTotal + readValue(row, "commLenaTotal"),
        }),
        {
          marketBookmaker: 0,
          marketFancy: 0,
          denaBookmaker: 0,
          denaFancy: 0,
          denaTotal: 0,
          lenaBookmaker: 0,
          lenaFancy: 0,
          lenaTotal: 0,
        }
      ),
    [rows]
  );

  function renderDetailTable(childRows, parentKey) {
    return (
      <div className="commission-userwise-detail-wrap">
        <table className="commission-userwise-detail-table">
          <thead>
            <tr className="commission-userwise-detail-groups">
              <th colSpan={4} aria-label="Summary" />
              <th colSpan={3}>Market</th>
              <th colSpan={3}>Commission Dena</th>
              <th colSpan={3}>Commission Lena</th>
              <th colSpan={2} aria-label="Partnership" />
            </tr>
            <tr>
              <th>Username</th>
              <th>Profit/Loss</th>
              <th>Commission</th>
              <th>Total P/L</th>
              <th>Odds</th>
              <th>Bookmaker</th>
              <th>Fancy</th>
              <th>Bookmaker</th>
              <th>Fancy</th>
              <th>Total</th>
              <th>Bookmaker</th>
              <th>Fancy</th>
              <th>Total</th>
              <th>Own</th>
              <th>Upperline</th>
            </tr>
          </thead>
          <tbody>
            {childRows.length ? (
              childRows.map((child, index) =>
                renderDetailRow(
                  child,
                  `${parentKey}-${child?.userId || child?.username || index}`
                )
              )
            ) : (
              <tr>
                <td colSpan={15} className="commission-userwise-empty-detail">
                  No child records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  function renderDetailRow(row, key) {
    const userKey = String(row?.userId);
    const isDrillable = canDrillInto(row);
    const isExpanded = Boolean(expandedUsers[userKey]);
    const childRows = childrenByParent[userKey] || [];
    const isLoadingChildren = Boolean(loadingUsers[userKey]);

    return (
      <React.Fragment key={key}>
        <tr>
          <td>
            {isDrillable ? (
              <>
                <button
                  type="button"
                  className="commission-user-link"
                  disabled={loading}
                  onClick={() => openUserReport(row)}
                  title="Open this user's report"
                >
                  {row?.userName || row?.username || "-"}
                </button>{" "}
                <button
                  type="button"
                  className="commission-user-toggle"
                  disabled={loading || isLoadingChildren}
                  onClick={() => toggleUser(row)}
                  title="Show or hide this user's direct children"
                >
                  [{isExpanded ? "−" : "+"}]
                </button>
              </>
            ) : (
              row?.userName || row?.username || "-"
            )}
          </td>
          <AmountCell value={row?.profitLoss} />
          <AmountCell value={row?.commission} />
          <AmountCell value={row?.totalPL} />
          <AmountCell value={row?.odds} />
          <AmountCell value={row?.bookmaker} />
          <AmountCell value={row?.fancy} />
          <AmountCell value={row?.commDenaBookmaker} />
          <AmountCell value={row?.commDenaFancy} />
          <AmountCell value={row?.commDenaTotal} />
          <AmountCell value={row?.commLenaBookmaker} />
          <AmountCell value={row?.commLenaFancy} />
          <AmountCell value={row?.commLenaTotal} />
          <AmountCell value={row?.own} />
          <AmountCell value={row?.upperline} />
        </tr>
        {isExpanded &&
          <tr className="commission-userwise-nested-detail">
            <td colSpan={15}>{renderDetailTable(childRows, key)}</td>
          </tr>}
      </React.Fragment>
    );
  }

  const renderRow = (row, key) => {
    const userKey = String(row?.userId);
    const isDrillable = canDrillInto(row);
    const isExpanded = Boolean(expandedUsers[userKey]);
    const childRows = childrenByParent[userKey] || [];
    const isLoadingChildren = Boolean(loadingUsers[userKey]);

    return (
      <React.Fragment key={key}>
        <tr>
          <td>
            {isDrillable ? (
              <>
                <button
                  type="button"
                  className="commission-user-link"
                  disabled={loading}
                  onClick={() => openUserReport(row)}
                  title="Open this user's report"
                >
                  {row?.userName || row?.username || "-"}
                </button>{" "}
                <button
                  type="button"
                  className="commission-user-toggle"
                  disabled={loading || isLoadingChildren}
                  onClick={() => toggleUser(row)}
                  title="Show or hide this user's details"
                >
                  [{isExpanded ? "−" : "+"}]
                </button>
              </>
            ) : (
              row?.userName || row?.username || "-"
            )}
          </td>
          <AmountCell value={row?.bookmaker} />
          <AmountCell value={row?.fancy} />
          <AmountCell value={row?.commDenaBookmaker} />
          <AmountCell value={row?.commDenaFancy} />
          <AmountCell value={row?.commDenaTotal} />
          <AmountCell value={row?.commLenaBookmaker} />
          <AmountCell value={row?.commLenaFancy} />
          <AmountCell value={row?.commLenaTotal} />
        </tr>
        {isExpanded && (
          <tr className="commission-userwise-detail-row">
            <td colSpan={9}>{renderDetailTable(childRows, key)}</td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="main commission-userwise-report-page">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 main-container">
            <div className="listing-grid">
              <div className="detail-row">
                <h2>Commission Userwise Report</h2>
                <div id="form-search">
                  <div className="m-t-20">
                    <div className="row">
                      <div className="col-md-2">
                        <div className="form-group">
                          <label htmlFor="commission-from-date">From</label>
                          <input
                            id="commission-from-date"
                            className="form-control"
                            type="date"
                            value={fromDate}
                            onChange={(event) => setFromDate(event.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <label htmlFor="commission-to-date">To</label>
                          <input
                            id="commission-to-date"
                            className="form-control"
                            type="date"
                            value={toDate}
                            min={fromDate}
                            onChange={(event) => setToDate(event.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label style={{ width: "100%" }}>&nbsp;</label>
                        <button
                          type="button"
                          className="btn btn-diamond"
                          onClick={loadReport}
                          disabled={loading}
                        >
                          {loading ? "Loading..." : "Load"}
                        </button>
                        {nameDrillPath.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-diamond"
                            onClick={goBackFromNameReport}
                            disabled={loading}
                          >
                            Back
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="table-responsive data-table commission-report-table-wrap">
                <table className="custom-table table table-striped commission-report-table">
                  <thead>
                    <tr className="commission-group-heading">
                      <th colSpan={3}>Market</th>
                      <th colSpan={3}>Commission Dena</th>
                      <th colSpan={3}>Commission Lena</th>
                    </tr>
                    <tr>
                      <th>Username</th>
                      <th>Bookmaker</th>
                      <th>Fancy</th>
                      <th>Bookmaker</th>
                      <th>Fancy</th>
                      <th>Total</th>
                      <th>Bookmaker</th>
                      <th>Fancy</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0 ? (
                      rows.map((row, index) =>
                        renderRow(row, row?.userId || row?.uid || row?.username || index)
                      )
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center">
                          {loading ? "Loading report..." : "No records found"}
                        </td>
                      </tr>
                    )}
                    <tr className="commission-grand-total">
                      <th>Grand Total</th>
                      <th className={amountClassName(totals.marketBookmaker)}>{totals.marketBookmaker}</th>
                      <th className={amountClassName(totals.marketFancy)}>{totals.marketFancy}</th>
                      <th className={amountClassName(totals.denaBookmaker)}>{totals.denaBookmaker}</th>
                      <th className={amountClassName(totals.denaFancy)}>{totals.denaFancy}</th>
                      <th className={amountClassName(totals.denaTotal)}>{totals.denaTotal}</th>
                      <th className={amountClassName(totals.lenaBookmaker)}>{totals.lenaBookmaker}</th>
                      <th className={amountClassName(totals.lenaFancy)}>{totals.lenaFancy}</th>
                      <th className={amountClassName(totals.lenaTotal)}>{totals.lenaTotal}</th>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
